import { useState, useEffect } from 'react';
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AirportContactFinder = ({
  countriesByContinent,
  fetchCountries,
  searchAirports,
  airportSearchType,
  setAirportSearchType,
  selectedContinent,
  setSelectedContinent,
  selectedCountry,
  setSelectedCountry,
  icaoSearch,
  setIcaoSearch,
  airportNameSearch,
  setAirportNameSearch,
  airportResults,
  searchingAirports
}) => {
  // Local state for analyses
  const [analyzedAirports, setAnalyzedAirports] = useState({}); // {icao: analysisData}
  const [expandedAirports, setExpandedAirports] = useState({}); // {icao: true/false}
  const [analyzingIcao, setAnalyzingIcao] = useState(null);
  const [findingContacts, setFindingContacts] = useState(null);
  
  // Load countries when component mounts
  useEffect(() => {
    if (Object.keys(countriesByContinent).length === 0) {
      fetchCountries();
    }
  }, []);
  
  // Check for existing analyses when results change
  useEffect(() => {
    const checkAnalyses = async () => {
      for (const airport of airportResults) {
        if (!analyzedAirports[airport.icao]) {
          try {
            const response = await axios.get(`${API}/marketing/airports/${airport.icao}/analysis`, {
              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            if (response.data) {
              setAnalyzedAirports(prev => ({
                ...prev,
                [airport.icao]: response.data
              }));
            }
          } catch (error) {
            // No existing analysis, that's fine
          }
        }
      }
    };
    
    if (airportResults.length > 0) {
      checkAnalyses();
    }
  }, [airportResults]);
  
  const handleAnalyze = async (airport, refresh = false) => {
    setAnalyzingIcao(airport.icao);
    try {
      const formData = new FormData();
      if (refresh) {
        formData.append('refresh', 'true');
      }
      
      const response = await axios.post(
        `${API}/marketing/airports/${airport.icao}/analyze`,
        formData,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      setAnalyzedAirports(prev => ({
        ...prev,
        [airport.icao]: response.data
      }));
      
      setExpandedAirports(prev => ({
        ...prev,
        [airport.icao]: true
      }));
    } catch (error) {
      console.error('Analysis error:', error);
      alert('Failed to analyze airport');
    } finally {
      setAnalyzingIcao(null);
    }
  };
  
  const handleFindContacts = async (icao) => {
    setFindingContacts(icao);
    try {
      const response = await axios.post(
        `${API}/marketing/airports/${icao}/contacts`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      // Update the analyzed airport with contacts
      setAnalyzedAirports(prev => ({
        ...prev,
        [icao]: {
          ...prev[icao],
          contacts_analysis: response.data.contacts_analysis,
          contacts_analyzed_at: new Date().toISOString()
        }
      }));
    } catch (error) {
      console.error('Contacts error:', error);
      alert(error.response?.data?.detail || 'Failed to find contacts');
    } finally {
      setFindingContacts(null);
    }
  };
  
  const toggleExpand = (icao) => {
    setExpandedAirports(prev => ({
      ...prev,
      [icao]: !prev[icao]
    }));
  };
  
  // Convert feet to meters
  const feetToMeters = (feet) => {
    return feet ? Math.round(feet * 0.3048) : 0;
  };
  
  // Function to parse and linkify emails and URLs in text
  const linkifyText = (text) => {
    if (!text) return text;
    
    // Regular expressions for emails and URLs
    const emailRegex = /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/gi;
    const urlRegex = /(https?:\/\/[^\s]+)/gi;
    const phoneRegex = /(\+?\d{1,4}[\s-]?\(?\d{1,4}\)?[\s-]?\d{1,4}[\s-]?\d{1,9})/g;
    
    let result = text;
    
    // Replace URLs with clickable links
    result = result.replace(urlRegex, (url) => {
      return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline break-all">${url}</a>`;
    });
    
    // Replace emails with clickable mailto links
    result = result.replace(emailRegex, (email) => {
      return `<a href="mailto:${email}" class="text-blue-600 hover:text-blue-800 underline font-medium">${email}</a>`;
    });
    
    // Highlight phone numbers (not clickable but make them stand out)
    result = result.replace(phoneRegex, (phone) => {
      return `<span class="font-medium text-green-700">${phone}</span>`;
    });
    
    return result;
  };

  const renderSearchForm = () => (
    <Card>
      <CardHeader>
        <CardTitle>Search Airports</CardTitle>
        <CardDescription>
          Search by country, ICAO code, or airport name. ICAO code takes priority if entered.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Search Type Selector */}
        <div className="flex gap-2">
          <Button
            variant={airportSearchType === 'country' ? 'default' : 'outline'}
            onClick={() => {
              setAirportSearchType('country');
              setIcaoSearch('');
              setAirportNameSearch('');
            }}
            size="sm"
          >
            🌍 By Country
          </Button>
          <Button
            variant={airportSearchType === 'icao' ? 'default' : 'outline'}
            onClick={() => {
              setAirportSearchType('icao');
              setSelectedContinent('');
              setSelectedCountry('');
              setAirportNameSearch('');
            }}
            size="sm"
          >
            🏷️ By ICAO Code
          </Button>
          <Button
            variant={airportSearchType === 'name' ? 'default' : 'outline'}
            onClick={() => {
              setAirportSearchType('name');
              setSelectedContinent('');
              setSelectedCountry('');
              setIcaoSearch('');
            }}
            size="sm"
          >
            🔍 By Airport Name
          </Button>
        </div>

        {/* Country Search */}
        {airportSearchType === 'country' && (
          <div className="space-y-4">
            <div>
              <Label>Select Continent</Label>
              <Select value={selectedContinent} onValueChange={(value) => {
                setSelectedContinent(value);
                setSelectedCountry('');
              }}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a continent" />
                </SelectTrigger>
                <SelectContent>
                  {Object.keys(countriesByContinent).sort().map(continent => (
                    <SelectItem key={continent} value={continent}>
                      {continent}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedContinent && (
              <div>
                <Label>Select Country</Label>
                <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a country" />
                  </SelectTrigger>
                  <SelectContent className="max-h-64">
                    {countriesByContinent[selectedContinent]?.map(country => (
                      <SelectItem key={country.code} value={country.code}>
                        {country.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        )}

        {/* ICAO Search */}
        {airportSearchType === 'icao' && (
          <div>
            <Label htmlFor="icao-search">ICAO Code</Label>
            <Input
              id="icao-search"
              placeholder="e.g., EKCH, KLAX, OMDB"
              value={icaoSearch}
              onChange={(e) => setIcaoSearch(e.target.value.toUpperCase())}
              maxLength={4}
            />
            <p className="text-xs text-muted-foreground mt-1">Enter 4-letter ICAO code</p>
          </div>
        )}

        {/* Name Search */}
        {airportSearchType === 'name' && (
          <div>
            <Label htmlFor="name-search">Airport Name or City</Label>
            <Input
              id="name-search"
              placeholder="e.g., Copenhagen, Heathrow, Dubai"
              value={airportNameSearch}
              onChange={(e) => setAirportNameSearch(e.target.value)}
            />
            <p className="text-xs text-muted-foreground mt-1">Search by airport name or city</p>
          </div>
        )}

        <Button
          onClick={searchAirports}
          disabled={searchingAirports}
          className="w-full"
        >
          {searchingAirports ? 'Searching...' : '🔍 Search Airports'}
        </Button>
      </CardContent>
    </Card>
  );

  const renderAirportResults = () => {
    if (airportResults.length === 0) return null;

    return (
      <Card>
        <CardHeader>
          <CardTitle>Search Results ({airportResults.length})</CardTitle>
          <CardDescription>Click to analyze airports and view detailed information</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {airportResults.map((airport, index) => {
              const hasAnalysis = !!analyzedAirports[airport.icao];
              const isExpanded = expandedAirports[airport.icao];
              const isAnalyzing = analyzingIcao === airport.icao;
              const isFindingContacts = findingContacts === airport.icao;
              const analysis = analyzedAirports[airport.icao];
              
              return (
                <div
                  key={`${airport.icao}-${index}`}
                  className="border rounded-lg overflow-hidden"
                >
                  {/* Airport Summary */}
                  <div className="p-4 hover:bg-slate-50 transition-colors">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {hasAnalysis && (
                            <button
                              onClick={() => toggleExpand(airport.icao)}
                              className="text-indigo-600 hover:text-indigo-800"
                            >
                              {isExpanded ? '▼' : '▶'}
                            </button>
                          )}
                          <h3 className="font-semibold text-lg">{airport.name}</h3>
                          {airport.iata && (
                            <Badge variant="outline">{airport.iata}</Badge>
                          )}
                          {hasAnalysis && (
                            <Badge variant="secondary">✓ Analyzed</Badge>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground space-y-1 ml-6">
                          <p><strong>ICAO:</strong> {airport.icao}</p>
                          <p><strong>Location:</strong> {airport.municipality}, {airport.country_code}</p>
                          {airport.elevation_ft && (
                            <p><strong>Elevation:</strong> {feetToMeters(airport.elevation_ft)} m ({airport.elevation_ft} ft)</p>
                          )}
                          <p><strong>Type:</strong> {airport.type?.replace('_', ' ')}</p>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <Button
                          onClick={() => handleAnalyze(airport, hasAnalysis)}
                          disabled={isAnalyzing}
                          size="sm"
                          variant={hasAnalysis ? 'outline' : 'default'}
                        >
                          {isAnalyzing ? 'Loading...' : hasAnalysis ? '🔄 Refresh' : '📋 More Information'}
                        </Button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Expanded Analysis */}
                  {hasAnalysis && isExpanded && analysis && (
                    <div className="border-t bg-slate-50 p-4">
                      {renderInlineAnalysis(analysis, airport.icao, isFindingContacts)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderInlineAnalysis = (analysis, icao, isFindingContacts) => {
    const airport = analysis.airport_data;
    const links = analysis.links;

    return (
      <div className="space-y-4">
        {/* Quick Info */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-3 bg-white rounded-lg border">
          <div>
            <p className="text-xs text-muted-foreground">Type</p>
            <p className="font-medium text-sm">{airport.type?.replace('_', ' ')}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Elevation</p>
            <p className="font-medium text-sm">{feetToMeters(airport.elevation_ft)} m</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Coordinates</p>
            <p className="font-medium text-sm">{airport.latitude}, {airport.longitude}</p>
          </div>
        </div>

        {/* Runways - in METRIC */}
        {airport.runways && airport.runways.length > 0 && (
          <div>
            <h4 className="font-semibold mb-2 text-sm">Runways ({airport.runways.length})</h4>
            <div className="space-y-2">
              {airport.runways.map((runway, idx) => (
                <div key={idx} className="p-3 border rounded-md text-sm bg-white">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{runway.designation}</span>
                    <Badge variant={runway.lighted ? 'default' : 'secondary'} className="text-xs">
                      {runway.lighted ? '🔆 Lighted' : 'Not Lighted'}
                    </Badge>
                  </div>
                  <div className="text-muted-foreground mt-1 text-xs">
                    {feetToMeters(runway.length_ft)}m × {feetToMeters(runway.width_ft)}m • {runway.surface}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Basic Analysis */}
        <div className="bg-white p-3 rounded-lg border">
          <h4 className="font-semibold mb-2 text-sm">Infrastructure Analysis</h4>
          <div className="prose prose-sm max-w-none">
            <div className="whitespace-pre-wrap text-xs text-muted-foreground">{analysis.basic_analysis}</div>
          </div>
        </div>

        {/* Contacts Section */}
        {analysis.contacts_analysis ? (
          <div className="bg-white p-3 rounded-lg border">
            <h4 className="font-semibold mb-2 text-sm">✉️ Contact Information</h4>
            <div className="prose prose-sm max-w-none">
              <div 
                className="text-xs text-muted-foreground leading-relaxed"
                dangerouslySetInnerHTML={{ 
                  __html: linkifyText(analysis.contacts_analysis).replace(/\n/g, '<br/>') 
                }}
              />
            </div>
            <div className="mt-3 pt-3 border-t">
              <Button variant="outline" size="sm" disabled>
                ✉️ Email Contacts (Coming Soon)
              </Button>
            </div>
          </div>
        ) : (
          <div className="bg-white p-3 rounded-lg border">
            <h4 className="font-semibold mb-2 text-sm">Need Contact Information?</h4>
            <p className="text-xs text-muted-foreground mb-3">
              Run a deep search to find key personnel and contact details for this airport.
            </p>
            <Button
              onClick={() => handleFindContacts(icao)}
              disabled={isFindingContacts}
              size="sm"
              variant="default"
            >
              {isFindingContacts ? 'Searching...' : '🔍 Find Contacts'}
            </Button>
          </div>
        )}

        {/* Useful Links */}
        <div>
          <h4 className="font-semibold mb-2 text-sm">Quick Links</h4>
          <div className="flex flex-wrap gap-2">
            {links.google_maps && (
              <Button variant="outline" size="sm" asChild>
                <a href={links.google_maps} target="_blank" rel="noopener noreferrer" className="text-xs">
                  📍 Maps
                </a>
              </Button>
            )}
            {links.google_images && (
              <Button variant="outline" size="sm" asChild>
                <a href={links.google_images} target="_blank" rel="noopener noreferrer" className="text-xs">
                  🖼️ Images
                </a>
              </Button>
            )}
            {links.ourairports && (
              <Button variant="outline" size="sm" asChild>
                <a href={links.ourairports} target="_blank" rel="noopener noreferrer" className="text-xs">
                  ✈️ OurAirports
                </a>
              </Button>
            )}
            {links.official_website && links.official_website !== '' && (
              <Button variant="outline" size="sm" asChild>
                <a href={links.official_website} target="_blank" rel="noopener noreferrer" className="text-xs">
                  🌐 Website
                </a>
              </Button>
            )}
            {links.wikipedia && links.wikipedia !== '' && (
              <Button variant="outline" size="sm" asChild>
                <a href={links.wikipedia} target="_blank" rel="noopener noreferrer" className="text-xs">
                  📖 Wikipedia
                </a>
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Search Form */}
      {renderSearchForm()}

      {/* Results with Inline Analysis */}
      {renderAirportResults()}
    </div>
  );
};
